import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import RegistrationGuard from './RegistrationGuard';
import { useRegistration } from './RegistrationContext';
import RegistrationStepper from './RegistrationStepper';
import { useToast } from './context/ToastContext';
import { isBlank, withError } from './utils/fieldErrors';
import { fo } from './styles/frontOfficeModuleClasses';

const RELATIONSHIP_OPTIONS = [
  { value: 'Self', label: 'Self (mine)' },
  { value: 'Parent', label: 'Parent' },
  { value: 'Guardian', label: 'Guardian' },
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Other', label: 'Other' },
];

function todayIsoDate() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function patientFullName(draft) {
  return [draft.first_name, draft.last_name].filter(Boolean).join(' ').trim();
}

function Step3Form() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { draft, updateField, patchDraft } = useRegistration();
  const [shownErrors, setShownErrors] = useState({});

  const fullName = useMemo(() => patientFullName(draft), [draft.first_name, draft.last_name]);
  const isSelf = draft.consent_relationship === 'Self';

  useEffect(() => {
    const registrationDate = todayIsoDate();
    const patch = {};
    if (draft.consent_date !== registrationDate) patch.consent_date = registrationDate;
    if (!draft.consent_relationship) patch.consent_relationship = 'Self';
    if (!draft.consent_patient_full_name && fullName) {
      patch.consent_patient_full_name = fullName;
    }
    if (
      (!draft.consent_signer_name || draft.consent_relationship === 'Self') &&
      fullName &&
      (draft.consent_relationship === 'Self' || !draft.consent_relationship)
    ) {
      patch.consent_signer_name = fullName;
    }
    // OTP is not required for registration — clear any leftover session flags.
    patch.consent_otp_sent = false;
    patch.consent_otp_verified = false;
    if (Object.keys(patch).length) patchDraft(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!fullName) return;
    if (!draft.consent_patient_full_name) {
      updateField('consent_patient_full_name', fullName);
    }
  }, [fullName, draft.consent_patient_full_name, updateField]);

  function handleRelationshipChange(value) {
    if (value === 'Self') {
      patchDraft({
        consent_relationship: value,
        consent_signer_name: draft.consent_patient_full_name || fullName,
      });
      return;
    }
    patchDraft({
      consent_relationship: value,
    });
  }

  const invalid = {
    consent_patient_full_name: isBlank(draft.consent_patient_full_name),
    consent_signer_name: isSelf ? false : isBlank(draft.consent_signer_name),
    consent_relationship: !isSelf && isBlank(draft.consent_relationship),
    consent_date: isBlank(draft.consent_date),
  };
  const err = (key) => Boolean(shownErrors[key] && invalid[key]);

  function markErrors(keys) {
    setShownErrors(
      keys.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {})
    );
  }

  function clearShownErrors() {
    setShownErrors({});
  }

  function onNext(e) {
    e.preventDefault();
    if (invalid.consent_patient_full_name) {
      markErrors(['consent_patient_full_name']);
      showToast('Patient / dependant full name is required.', 'error');
      return;
    }
    if (!isSelf && invalid.consent_relationship) {
      markErrors(['consent_relationship']);
      showToast('Select relationship to patient.', 'error');
      return;
    }
    if (!isSelf && invalid.consent_signer_name) {
      markErrors(['consent_signer_name']);
      showToast('Patient name / Guardian is required.', 'error');
      return;
    }
    if (isSelf) {
      const signerName = (draft.consent_signer_name || draft.consent_patient_full_name || fullName).trim();
      if (signerName && signerName !== (draft.consent_signer_name || '').trim()) {
        patchDraft({ consent_signer_name: signerName });
      }
    }
    if (invalid.consent_date) {
      markErrors(['consent_date']);
      showToast('Consent date is required.', 'error');
      return;
    }
    clearShownErrors();
    navigate('/front_office/registration/step-4');
  }

  return (
    <div className={fo.page}>
      <div className={fo.registrationIntro}>
        <header className={fo.header}>
          <p className={fo.kicker}>New admission</p>
          <h1 className={fo.title}>Patient registration</h1>
          <p className={fo.sub}>Step 3: Consent</p>
        </header>
        <RegistrationStepper activeStep={3} />
        <div className={fo.progressWrap}>
          <div className={fo.progressTrack} aria-hidden>
            <div className={fo.progressFill} style={{ width: '75%' }} />
          </div>
          <span className={fo.progressLabel}>75% complete</span>
        </div>
      </div>

      <form onSubmit={onNext} className={fo.form} noValidate>
        <article className={fo.sectionPanel}>
          <h3 className={fo.sectionTitle}>Treatment consent</h3>
          <p className="mt-1 text-sm text-slate-600">
            Complete consent details to register. Cell phone OTP verification is not required.
          </p>
          <div
            className={`mt-4 rounded-xl border bg-slate-50/80 p-4 text-sm leading-relaxed text-slate-700 ${
              err('consent_patient_full_name') ? 'border-red-500' : 'border-slate-200'
            }`}
          >
            <p>
              I hereby permit dental treatment for myself / dependant:{' '}
              <input
                type="text"
                aria-label="Patient or dependant full name"
                className={withError(
                  `${fo.input} mt-2 inline-block max-w-full font-semibold`,
                  err('consent_patient_full_name')
                )}
                value={draft.consent_patient_full_name || ''}
                onChange={(e) => {
                  const name = e.target.value;
                  const patch = {
                    consent_patient_full_name: name,
                  };
                  if (isSelf) patch.consent_signer_name = name;
                  patchDraft(patch);
                  setShownErrors((prev) => {
                    const next = { ...prev };
                    delete next.consent_patient_full_name;
                    return next;
                  });
                }}
              />
            </p>
            <p className="mt-3">
              I take full responsibility for the payment for the care rendered. I give the dentist
              the discretion to the best treatment that can be offered.
            </p>
          </div>

          <div className={`${fo.fieldRow} mt-5`}>
            <p className={fo.field}>
              <label className={fo.label} htmlFor="fo-consent-relationship">
                Relationship to patient{isSelf ? '' : ' *'}
              </label>
              <select
                id="fo-consent-relationship"
                className={withError(fo.select, err('consent_relationship'))}
                value={draft.consent_relationship || ''}
                onChange={(e) => handleRelationshipChange(e.target.value)}
              >
                <option value="" disabled>
                  Select relationship
                </option>
                {RELATIONSHIP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </p>
            <p className={fo.field}>
              <label className={fo.label} htmlFor="fo-consent-date">
                Date *
              </label>
              <input
                id="fo-consent-date"
                type="date"
                className={withError(fo.input, err('consent_date'))}
                value={draft.consent_date || todayIsoDate()}
                readOnly
                aria-readonly="true"
              />
            </p>
          </div>

          <p className={`${fo.field} mt-4`}>
            <label className={fo.label} htmlFor="fo-consent-signer">
              Patient name / Guardian{isSelf ? '' : ' *'}
            </label>
            <input
              id="fo-consent-signer"
              className={withError(fo.input, err('consent_signer_name'))}
              value={draft.consent_signer_name || ''}
              readOnly={isSelf}
              onChange={(e) => {
                if (isSelf) return;
                patchDraft({
                  consent_signer_name: e.target.value,
                });
              }}
            />
            <span className="mt-1 block text-xs text-slate-500">
              {isSelf
                ? 'Locked to the patient name because relationship is Self (mine).'
                : 'Editable when signing for a dependant — change if the relationship is not Self.'}
            </span>
          </p>
        </article>

        <footer className={fo.actions}>
          <Link to="/front_office/registration/step-2" className={fo.btnOutline}>
            Back
          </Link>
          <button type="submit" className={fo.btnPrimary}>
            Continue
          </button>
        </footer>
      </form>
    </div>
  );
}

export default function PatientRegistrationStep3Page() {
  return (
    <RegistrationGuard>
      <Step3Form />
    </RegistrationGuard>
  );
}
